
import BaseInputOutlined from '@/components/inputs/BaseInputOutlined.vue'

import { computed, defineComponent, ref, onMounted, reactive } from 'vue'
import api from '../../../api/api'
import { useProject } from '../../../hooks/useProject'
import { useDateMenu } from '../../../hooks/useDateMenu'
import moment from 'moment'
import Breadcrumbs from '../../../components/Breadcrumbs.vue'
import SpinnerLoader from '../../../components/SpinnerLoader.vue'
import { mapFilterToObjectFormat, mapFilterFromObjectFormat, getFilterListFromObjectFormat } from '@/utils'
import { downloadFile } from '@/utils'
import CustomActivatorSelect from '../../../components/inputs/CustomActivatorSelect.vue'
import { Connector, Project, ProjectType } from '@/types/main'
import { CONNECTOR_TYPES } from '@root/src/vars/general'
import { getStaffTurnoverReport } from '@root/src/api/chisai/GCB2'

interface TableHeader {
    text: string
    value: string
    align?: string
    sort?: (a: any, b: any) => number
    width?: string
    colspan?: number
    rowspan?: number
}

const numSort = (a, b) => {
    const aNum = a ? Number(a.toString().replace(/\s/g, '')) : 0
    const bNum = b ? Number(b.toString().replace(/\s/g, '')) : 0
    return aNum - bNum
}

export default defineComponent({
    components: {
        BaseInputOutlined,
        Breadcrumbs,
        SpinnerLoader,
        CustomActivatorSelect,
    },
    name: 'StaffTurnover',
    props: {},
    data: () => ({
        generalTurnoverTooltip: 'Считается как число уволившихся сотрудников разделить на среднесписочную численность сотрудников и умножить на 100%. Среднесписочная численность работников за период исчисляется путем суммирования списочной численности работников за каждый календарный день периода, т. е. с 1 по 30 или 31 число (для февраля - по 28 или 29 число), включая праздничные (нерабочие) и выходные дни, и деления полученной суммы на число календарных дней периода',
        loading: false,
        dashboardData: [],
        filialIds: [] as null | string[],
        // По умолчанию для обычных проектов оставляем старый список
        selectedPosition: ['beautician', 'manicurist', 'hairdresser'],
        dateMenu: false,
        dates: [] as string[],
        dateFormat: 'DD.MM.YYYY',
        datePickerDateFormat: 'YYYY-MM-DD',
        // Состояние сортировки для каждой таблицы
        sortState: {
            staff: { column: '', direction: 'none' },
            fullness: { column: '', direction: 'none' },
            averageBill: { column: '', direction: 'none' },
            price: { column: '', direction: 'none' }
        },
        // Исходные данные для восстановления сортировки
        originalData: {
            staff: [],
            fullness: [],
            averageBill: [],
            price: []
        },
        staffTableData: [] as any [],
        fullnessTableData: [] as any [],
        averageBillTableData: [] as any [],
        priceTableData: [] as any [],
        footerProps: { 'items-per-page-options': [15, 50, 100, -1] },
        checkTurnoverBounds: {} as any,
        hourRevenueTurnoverBounds: {} as any,
        occupancyTurnoverBounds: {} as any,
    }),
    computed: {
        breadcrumbs(): any {
            return [
                {
                    text: this.project.name,
                    to: `/project/${this.project.id}`,
                },
                {
                    text: 'Отчет по текучести кадров',
                    to: '',
                    disabled: true,
                },
            ]
        },
        project(): Project {
            return this.$store.getters.projectById(this.$router.currentRoute.params.id)
        },
        isHrProject(): boolean {
            return this.project.connectors.find((con) => con.connectorType === CONNECTOR_TYPES.GCB2)?.hrProject || false
        },
        projectType(): ProjectType {
            return this.$store.getters.projectType;
        },
        pseudoNetworkProjects(): Project[] {
            return this.$store.getters.pseudoNetworkProjects || []
        },
        networkProjects(): Project[] {
            return this.$store.getters.networkProjects || []
        },
        filialList(): any[] {
            if(this.projectType === 'pseudo_network') {
                return this.pseudoNetworkProjects?.filter(el => !el?.isMain) || []
            } else {
                return this.networkProjects?.filter(el => !el?.isMain) || []
            }
        },
        positionList(): {name: string, id: string}[] {
            if(['XSTM8F6WE', 'X9FUHP3L5'].includes(this.project.id)) {
                return [
                    {name: 'Ногтевой мастер', id: 'nailMaster'},
                    {name: 'Бровист/лашмейкер', id: 'browLashMaster'},
                    {name: 'Массажист', id: 'masseur'},
                    {name: 'Визажист-стилист/Парикмахер', id: 'stylistHairdresser'},
                    {name: 'Мастер шугаринга', id: 'sugaringMaster'},
                    {name: 'Другое', id: 'other'},
                ]
            }
            return [
                {name: 'Администратор', id: 'admin'},
                {name: 'Косметолог', id: 'beautician'},
                {name: 'Мастер маникюра', id: 'manicurist'},
                {name: 'Парикмахер', id: 'hairdresser'},
                {name: 'Другое', id: 'other'},
            ]
        },
        defaultSelectedPosition(): string[] {
            // Для проекта XSTM8F6WE — все варианты, кроме "Другое"
            if (['XSTM8F6WE', 'X9FUHP3L5'].includes(this.project.id)) {
                return this.positionList
                    .filter(pos => pos.id !== 'other')
                    .map(pos => pos.id)
            }
            // Для остальных проектов — как было раньше
            return ['beautician', 'manicurist', 'hairdresser']
        },
        filialInputString(): string {
            if(this.filialIds.length === this.filialList.length) {
                return `Выбраны все (${this.filialIds.length})`
            } else {
                return `Филиалы(${this.filialIds.length})`
            }
        },
        dateRangeText(): string {
            const formatedDates = this.sortedDates.map(date =>
                moment(date, this.datePickerDateFormat).format(this.dateFormat)
            )
            return formatedDates.join(' - ')
        },
        positionInputString(): string {
            if(this.selectedPosition.length === this.positionList.length) {
                return `Все должности(${this.selectedPosition.length})`
            } else {
                return `Должности(${this.selectedPosition.length})`
            }
        },
        sortedDates(): string[] {
            return [
                ...this.dates.sort(
                    (a, b) =>
                        moment(a, this.datePickerDateFormat).unix() -
                        moment(b, this.datePickerDateFormat).unix()
                ),
            ]
        },
        staffTableHeaders(): TableHeader[] {
            return [
                { text: 'Филиал', value: 'projectName', width: '120px' },
                {
                    text: 'Общая текучесть кадров',
                    value: 'generalStaffTurnover',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: 'Отклонение общей текучести от ср. по проектам',
                    value: 'deviationFromAvgTurnover',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '0+м',
                    value: 'hrGeneralStaffTurnover1',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '6+м',
                    value: 'hrGeneralStaffTurnover2',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '1,5+г',
                    value: 'hrGeneralStaffTurnover3',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: 'Средний стаж сотрудников (в годах)',
                    value: 'meanWorkTime',
                    align: 'right',
                    sort: numSort,
                },
            ]
        },
        fullnessTableHeaders(): TableHeader[] {
            return [
                { 
                    text: 'Филиал', 
                    value: 'projectName', 
                    width: '120px',
                    align: 'center'
                },
                {
                    text: 'Общая текучесть кадров',
                    value: 'generalStaffTurnover',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: 'Отклонение общей текучести от ср. по проектам',
                    value: 'deviationFromAvgTurnover',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '0-33% уволено',
                    value: 'hrFiredDuringPeriod1',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '0-33% текучесть',
                    value: 'hrOccupancyStaffTurnover1',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '33%-45% уволено',
                    value: 'hrFiredDuringPeriod2',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '33%-45% текучесть',
                    value: 'hrOccupancyStaffTurnover2',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '45%+ уволено',
                    value: 'hrFiredDuringPeriod3',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '45%+ текучесть',
                    value: 'hrOccupancyStaffTurnover3',
                    align: 'right',
                    sort: numSort,
                },
            ]
        },
        averageBillTableHeaders(): TableHeader[] {
            return [
                { text: 'Филиал', value: 'projectName', width: '120px' },
                {
                    text: 'Общая текучесть кадров',
                    value: 'generalStaffTurnover',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: 'Отклонение общей текучести от ср. по проектам',
                    value: 'deviationFromAvgTurnover',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '0-1520 уволено',
                    value: 'hrFiredDuringPeriod1',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '0-1520 текучесть',
                    value: 'hrCheckStaffTurnover1',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '1520-1727 уволено',
                    value: 'hrFiredDuringPeriod2',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '1520-1727 текучесть',
                    value: 'hrCheckStaffTurnover2',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '1727+ уволено',
                    value: 'hrFiredDuringPeriod3',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '1727+ текучесть',
                    value: 'hrCheckStaffTurnover3',
                    align: 'right',
                    sort: numSort,
                },
            ]
        },
        priceTableHeaders(): TableHeader[] {
            return [
                { text: 'Филиал', value: 'projectName', width: '120px' },
                {
                    text: 'Общая текучесть кадров',
                    value: 'generalStaffTurnover',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: 'Отклонение общей текучести от ср. по проектам',
                    value: 'deviationFromAvgTurnover',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '0-1372 уволено',
                    value: 'hrFiredDuringPeriod1',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '0-1372 текучесть',
                    value: 'hrHourRevenueStaffTurnover1',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '1372-1593 уволено',
                    value: 'hrFiredDuringPeriod2',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '1372-1593 текучесть',
                    value: 'hrHourRevenueStaffTurnover2',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '1593+ уволено',
                    value: 'hrFiredDuringPeriod3',
                    align: 'right',
                    sort: numSort,
                },
                {
                    text: '1593+ текучесть',
                    value: 'hrHourRevenueStaffTurnover3',
                    align: 'right',
                    sort: numSort,
                },
            ]
        },
    },
    methods: {
        parseNumericValue(value: number | string): number {
            const normalized = typeof value === 'string' ? value.replace(',', '.') : value
            const num = Number(normalized)
            return isNaN(num) ? 0 : num
        },
        formatPercent(value: number | string): string {
            const num = this.parseNumericValue(value)
            const rounded = Math.round(num * 10) / 10
            const str = String(rounded).replace('.', ',')
            return `${str}%`
        },
        formatOneDecimal(value: number | string): string {
            const num = this.parseNumericValue(value)
            const rounded = Math.round(num * 10) / 10
            return String(rounded).replace('.', ',')
        },
        setDefaultDates() {
            this.dates = [
                moment()
                    .subtract(30, 'days')
                    .format(this.datePickerDateFormat),
                moment()
                    .subtract(0, 'days')
                    .format(this.datePickerDateFormat),
            ]
        },
        async initData(init: boolean, includeFileLink: boolean) {
            this.loading = true
            if (init){
                this.setDefaultDates()
            }
            const positionList = this.positionList.filter(el => this.selectedPosition.includes(el.id))
            if(init && this.pseudoNetworkProjects.length > 0) {
                this.filialIds.push(this.pseudoNetworkProjects[0].id)
            }
            const filters = {
                minDate: this.dates[0],
                maxDate: this.dates.length === 2 ? this.dates[1] : this.dates[0],
                filialList: this.filialIds,
                positionList
            }
            try {
                const {data, error} = await getStaffTurnoverReport({
                    projectId: this.project.id,
                    filters,
                    includeFileLink
                })
                
                const allTableData = data?.reportData || {}
                this.staffTableData = allTableData?.generalHrReportSheet || []
                this.fullnessTableData = allTableData?.occupancyTurnoverReportSheet || []
                this.averageBillTableData = allTableData?.checkTurnoverReportSheet || []
                this.priceTableData = allTableData?.staffHourRevenueTurnoverReportSheet || []

                const turnoverBounds = allTableData?.turnoverBounds || {}
                this.checkTurnoverBounds = turnoverBounds?.checkTurnoverBounds
                this.hourRevenueTurnoverBounds = turnoverBounds?.hourRevenueTurnoverBounds
                this.occupancyTurnoverBounds = turnoverBounds?.occupancyTurnoverBounds

                this.originalData.staff = [...this.staffTableData]
                this.originalData.fullness = [...this.fullnessTableData]
                this.originalData.averageBill = [...this.averageBillTableData]
                this.originalData.price = [...this.priceTableData]

                this.sortState = {
                    staff: { column: '', direction: 'none' },
                    fullness: { column: '', direction: 'none' },
                    averageBill: { column: '', direction: 'none' },
                    price: { column: '', direction: 'none' }
                }
                
                if (data?.fileSrc && includeFileLink) {
                    downloadFile(data.fileSrc, '')
                }
                if(error) {
                    this.$store.dispatch('callNotify', `Произошла ошибка`)
                }
            } finally {
                this.loading = false
            }
            
        },
        sortTable(tableName: string, column: string) {
            const currentSort = this.sortState[tableName]
            
            if (currentSort.column === column) {
                if (currentSort.direction === 'asc') {
                    currentSort.direction = 'desc'
                } else if (currentSort.direction === 'desc') {
                    currentSort.direction = 'none'
                    currentSort.column = ''
                } else {
                    currentSort.direction = 'asc'
                }
            } else {
                currentSort.column = column
                currentSort.direction = 'asc'
            }

            if (currentSort.direction === 'none') {
                this.restoreOriginalData(tableName)
            } else {
                this.sortData(tableName, column, currentSort.direction)
            }
        },
        
        sortData(tableName: string, column: string, direction: 'asc' | 'desc') {
            const dataKey = `${tableName}TableData`
            const data = this[dataKey]
            
            if (!data || !Array.isArray(data)) return
            
            const sortedData = [...data].sort((a, b) => {
                let aVal = a[column]
                let bVal = b[column]
                
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return direction === 'asc' ? aVal - bVal : bVal - aVal
                }

                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    aVal = aVal.toLowerCase()
                    bVal = bVal.toLowerCase()
                    if (direction === 'asc') {
                        return aVal.localeCompare(bVal)
                    } else {
                        return bVal.localeCompare(aVal)
                    }
                }
                
                return 0
            })
            
            this[dataKey] = sortedData
        },
        
        restoreOriginalData(tableName: string) {
            const dataKey = `${tableName}TableData`
            const originalKey = tableName
            
            this[dataKey] = [...(this.originalData[originalKey] || [])]
        },
        
        getSortIcon(tableName: string, column: string) {
            const sortState = this.sortState[tableName]
            if (sortState.column === column) {
                return sortState.direction === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down'
            }
            return 'mdi-arrow-up'
        },
        
        getBcgColorClass(value: number, tableName: string, colName: string): string {
            const tableDataCopy = this[tableName]
            
            const values = tableDataCopy.map(item => item[colName]).filter(val => val !== null && val !== undefined)
            
            if (values.length === 0) return 'transparent'
            
            const minValue = Math.min(...values)
            const maxValue = Math.max(...values)
            const range = maxValue - minValue
            
            if (range === 0) return 'transparent'
            
            const midPoint = (minValue + maxValue) / 2
            const distanceFromMid = Math.abs(value - midPoint)
            const maxDistance = range / 2
            
            if (distanceFromMid / maxDistance < 0.1) {
                return 'transparent'
            }
            
            const intensity = Math.min(distanceFromMid / maxDistance, 1)
            
            if (value < midPoint) {
                return this.generateGradientColor(intensity, 'green')
            } else {
                return this.generateGradientColor(intensity, 'red')
            }
        },
        
        generateGradientColor(intensity: number, type: 'green' | 'red'): string {
            const greenStart = '#eaf6f1' // light green
            const greenEnd = '#57bb8a' // dark green
            const redStart = '#fcedec' // dark red
            const redEnd = '#e67c73' // light red
            
            if (type === 'green') {
                return this.interpolateColor(greenStart, greenEnd, intensity)
            } else {
                return this.interpolateColor(redStart, redEnd, intensity)
            }
        },
        
        interpolateColor(color1: string, color2: string, factor: number): string {
            const hex1 = color1.replace('#', '')
            const hex2 = color2.replace('#', '')
            
            const r1 = parseInt(hex1.substring(0, 2), 16)
            const g1 = parseInt(hex1.substring(2, 4), 16)
            const b1 = parseInt(hex1.substring(4, 6), 16)
            
            const r2 = parseInt(hex2.substring(0, 2), 16)
            const g2 = parseInt(hex2.substring(2, 4), 16)
            const b2 = parseInt(hex2.substring(4, 6), 16)
            
            const r = Math.round(r1 + (r2 - r1) * factor)
            const g = Math.round(g1 + (g2 - g1) * factor)
            const b = Math.round(b1 + (b2 - b1) * factor)
            
            return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
        }
    },
    async mounted() {
        if(!this.isHrProject) {
            this.$router.push(`/project/${this.project.id}`)
        }
        // Дефолтный список должностей в зависимости от проекта
        this.selectedPosition = this.defaultSelectedPosition

        await this.initData(true, false)
        
        this.$nextTick(() => {
            this.originalData.staff = [...this.staffTableData]
            this.originalData.fullness = [...this.fullnessTableData]
            this.originalData.averageBill = [...this.averageBillTableData]
            this.originalData.price = [...this.priceTableData]
        })
    },
    setup(props, { emit }) {},
})
